import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Phone, RotateCw } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import {
  api,
  type Alert,
  type AuditEntry,
  type Company,
  type DialerScript,
  type EventEntry,
  type OutboxEntry,
  type Page,
  type RobotCall,
  type RobotReview,
  type SampleReport,
  type User,
  type UserErrorEntry,
} from "@/lib/api";
import { dateTime, relative } from "@/lib/format";
import { LogFiles } from "@/features/logfiles";
import {
  AudioPlayer,
  Badge,
  Button,
  Card,
  Combobox,
  type ComboboxItem,
  Dialog,
  Empty,
  ErrorNote,
  Field,
  Input,
  Notice,
  PageHeader,
  Select,
  SortableTh,
  Spinner,
  Stat,
  Table,
  TableFooter,
  Tabs,
  Td,
  Th,
  Toolbar,
} from "@/components/ui";
import { useClientTable, useServerTable } from "@/lib/table";
import { ExploreTab, ReviewButton, ReviewLine } from "@/features/explore";
import { useAuth, useMayUse, useScope } from "@/app/auth";

// --- logs ----------------------------------------------------------------

export function LogsPage() {
  // The log files hold every customer's traffic, so the tab is only offered
  // to the roles that may enter the machine room; the route refuses the rest.
  const mayReadFiles = useMayUse("system");
  const tabs = [
    { id: "errors", label: "Benutzerfehler" },
    { id: "events", label: "Mobilbox-Ereignisse" },
    { id: "audit", label: "Aktivität" },
    ...(mayReadFiles ? [{ id: "files", label: "Logs" }] : []),
  ];

  // The tab lives in the URL so that a file can be linked to:
  // /logs?tab=files&file=job.log.
  const [params, setParams] = useSearchParams();
  const requested = params.get("tab");
  const tab = tabs.some((t) => t.id === requested) ? requested! : "errors";
  const setTab = (id: string) =>
    setParams(id === "errors" ? {} : { tab: id }, { replace: true });

  return (
    <>
      <PageHeader
        title="Protokolle"
        description="Was passiert ist, und wer es getan hat."
      />

      <Tabs active={tab} onChange={setTab} tabs={tabs} />

      {tab === "errors" && <UserErrors />}
      {tab === "events" && <Events />}
      {tab === "audit" && <Audit />}
      {tab === "files" && mayReadFiles && <LogFiles />}
    </>
  );
}

function UserErrors() {
  const scope = useScope();
  const [kind, setKind] = useState("");

  // A log grows without bound, so the server pages and orders it: sorting the
  // two hundred rows that happened to be fetched is not sorting the log.
  const [total, setTotal] = useState(0);
  const table = useServerTable({
    total,
    initial: { key: "at", dir: "desc" },
    resetOn: [scope, kind],
  });

  const { data, isLoading } = useQuery({
    queryKey: ["logs", "user-errors", scope, kind, table.query],
    queryFn: () =>
      api<Page<UserErrorEntry>>(
        `/api/admin/logs/user-errors?${table.query}&kind=${kind}&companyId=${scope}`,
      ),
  });

  useEffect(() => {
    if (data) setTotal(data.total);
  }, [data]);

  return (
    <Card className="p-4">
      <Toolbar>
        <Select
          className="w-56"
          value={kind}
          onChange={(e) => setKind(e.target.value)}
        >
          <option value="">alle Arten</option>
          <option value="soundfile_missing">Aufnahme fehlt</option>
          <option value="not_eligible">nicht berechtigt</option>
          <option value="upload_failed">Einspielen fehlgeschlagen</option>
          <option value="repeat_failed">Wiederholung abgebrochen</option>
          <option value="dialer">Roboter</option>
        </Select>
      </Toolbar>

      {isLoading ? (
        <Spinner />
      ) : (
        <Table>
          <thead>
            <tr>
              <SortableTh table={table} sortKey="at" first="desc">
                Zeitpunkt
              </SortableTh>
              <SortableTh table={table} sortKey="userName">
                Person
              </SortableTh>
              <SortableTh table={table} sortKey="company">
                Firma
              </SortableTh>
              <SortableTh table={table} sortKey="kind">
                Art
              </SortableTh>
              <SortableTh table={table} sortKey="message">
                Meldung
              </SortableTh>
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((e) => (
              <tr key={e.id}>
                <Td className="whitespace-nowrap muted">{dateTime(e.at)}</Td>
                <Td>{e.userName || "—"}</Td>
                <Td className="muted">{e.company}</Td>
                <Td>
                  <Badge tone="warning">{errorKindLabel(e.kind)}</Badge>
                </Td>
                <Td title={e.detail}>{e.message}</Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {(data?.items ?? []).length === 0 && !isLoading && (
        <Empty title="Keine Fehler im gewählten Zeitraum" />
      )}
      <TableFooter table={table} noun="Fehler" />
    </Card>
  );
}

export function errorKindLabel(kind: string): string {
  switch (kind) {
    case "soundfile_missing":
      return "Aufnahme fehlt";
    case "not_eligible":
      return "nicht berechtigt";
    case "upload_failed":
      return "Einspielen fehlgeschlagen";
    case "repeat_failed":
      return "Wiederholung abgebrochen";
    case "dialer":
      return "Roboter";
    default:
      return kind;
  }
}

function Events() {
  const scope = useScope();

  const [total, setTotal] = useState(0);
  const table = useServerTable({
    total,
    initial: { key: "at", dir: "desc" },
    resetOn: [scope],
  });

  const { data, isLoading } = useQuery({
    queryKey: ["logs", "events", scope, table.query],
    queryFn: () =>
      api<Page<EventEntry>>(
        `/api/admin/logs/events?${table.query}&companyId=${scope}`,
      ),
  });

  useEffect(() => {
    if (data) setTotal(data.total);
  }, [data]);

  if (isLoading) return <Spinner />;

  return (
    <Card>
      <Table>
        <thead>
          <tr>
            <SortableTh table={table} sortKey="at" first="desc">
              Zeitpunkt
            </SortableTh>
            <SortableTh table={table} sortKey="userName">
              Person
            </SortableTh>
            <SortableTh table={table} sortKey="kind">
              Ereignis
            </SortableTh>
            <SortableTh table={table} sortKey="note">
              Anmerkung
            </SortableTh>
          </tr>
        </thead>
        <tbody>
          {(data?.items ?? []).map((e) => (
            <tr key={e.id}>
              <Td className="whitespace-nowrap muted">{dateTime(e.at)}</Td>
              <Td>{e.userName || "—"}</Td>
              <Td>{eventLabel(e.kind)}</Td>
              <Td className="muted">{e.note || "—"}</Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {(data?.items ?? []).length === 0 && (
        <Empty title="Noch nichts passiert" />
      )}
      <TableFooter table={table} noun="Ereignisse" />
    </Card>
  );
}

function eventLabel(kind: string): string {
  switch (kind) {
    case "vacation_greeting_applied":
      return "Abwesenheitsansage eingespielt";
    case "default_greeting_restored":
      return "Standardansage wiederhergestellt";
    case "default_greeting_applied":
      return "Standardansage aufgenommen";
    default:
      return kind;
  }
}

// The audit log holds three kinds of entry: what an administrator changed,
// when they signed in and out, and what they looked at. The last two are what
// the legacy admin.log had and lost on rotation.
const AUDIT_CATEGORIES = [
  { id: "", label: "alles" },
  { id: "change", label: "Änderungen" },
  { id: "session", label: "Anmeldungen" },
  { id: "view", label: "Ansichten" },
];

// The action names are machine words; these are the sentences for the ones
// that are not a plain "<record>.<verb>" -- the session events, and the looks
// that have no single record.
const ACTION_LABELS: Record<string, string> = {
  "session.signin": "angemeldet",
  "session.signin_failed": "Anmeldung fehlgeschlagen",
  "session.mfa_failed": "zweiter Faktor falsch",
  "session.signout": "abgemeldet",
  "session.scope": "Firma gewechselt",
  "passkey.add": "Passkey hinzugefügt",
  "passkey.rename": "Passkey umbenannt",
  "passkey.remove": "Passkey entfernt",
  "company.list": "Firmen aufgelistet",
  "company.view": "Firma angesehen",
  "user.list": "Benutzer aufgelistet",
  "user.view": "Benutzer angesehen",
  "user.export": "Benutzer exportiert",
  "user.impersonate": "als Benutzer angemeldet",
  "announcement.list": "Ansagen aufgelistet",
  "audio.view": "Aufnahmen angesehen",
  "auth.view": "Anmeldeeinstellungen angesehen",
  "report.view": "Firmenbericht angesehen",
  "job.view": "Auftrag angesehen",
  "trace.view": "Ablauf angesehen",
  "call.view": "Anruf angesehen",
  "log.view": "Protokoll angesehen",
  "stats.view": "Statistik angesehen",
  "billing.view": "Abrechnung angesehen",
  "definition.list": "Bausteine angesehen",
  "system.view": "System angesehen",
};

function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? action;
}

function isActivity(e: AuditEntry): boolean {
  return e.action.startsWith("session.") || /\.(view|list|export)$/.test(e.action);
}

// detailText is the one line the table shows for a look: the search term,
// which log or billing page, the reason a sign-in failed.
function detailText(e: AuditEntry): string {
  if (!isActivity(e) || !e.after || typeof e.after !== "object") return "";
  const d = e.after as Record<string, unknown>;
  const parts: string[] = [];
  if (typeof d.q === "string") parts.push(`„${d.q}“`);
  for (const k of ["log", "stats", "billing", "reason", "factor", "name"]) {
    if (typeof d[k] === "string") parts.push(String(d[k]));
  }
  return parts.join(" · ");
}

function Audit() {
  const scope = useScope();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [entry, setEntry] = useState<AuditEntry | null>(null);

  const [total, setTotal] = useState(0);
  const table = useServerTable({
    total,
    initial: { key: "at", dir: "desc" },
    resetOn: [scope, search, category],
  });

  const { data, isLoading } = useQuery({
    queryKey: ["logs", "audit", scope, search, category, table.query],
    queryFn: () =>
      api<Page<AuditEntry>>(
        `/api/admin/logs/audit?${table.query}&companyId=${scope}` +
          `&q=${encodeURIComponent(search)}&category=${category}`,
      ),
  });

  useEffect(() => {
    if (data) setTotal(data.total);
  }, [data]);

  return (
    <Card className="p-4">
      <Toolbar>
        <Select
          className="w-44"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          {AUDIT_CATEGORIES.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </Select>
        <Input
          className="w-64"
          placeholder="Aktion oder Person"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Toolbar>

      {isLoading ? (
        <Spinner />
      ) : (
        <Table>
          <thead>
            <tr>
              <SortableTh table={table} sortKey="at" first="desc">
                Zeitpunkt
              </SortableTh>
              <SortableTh table={table} sortKey="actorEmail">
                Wer
              </SortableTh>
              <SortableTh table={table} sortKey="action">
                Aktion
              </SortableTh>
              <SortableTh table={table} sortKey="targetType">
                Betrifft
              </SortableTh>
              <SortableTh table={table} sortKey="company">
                Firma
              </SortableTh>
              <Th />
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((e) => (
              <tr key={e.id}>
                <Td className="whitespace-nowrap muted">{dateTime(e.at)}</Td>
                <Td>{e.actorEmail || "System"}</Td>
                <Td>
                  {actionLabel(e.action)}
                  {ACTION_LABELS[e.action] && (
                    <span className="muted ml-2 font-mono text-xs">{e.action}</span>
                  )}
                </Td>
                <Td className="muted">
                  {e.targetType} {e.targetId || ""}
                  {detailText(e) && (
                    <span className="ml-1">{detailText(e)}</span>
                  )}
                </Td>
                <Td className="muted">{e.company}</Td>
                <Td className="text-right">
                  <Button size="sm" variant="ghost" onClick={() => setEntry(e)}>
                    Details
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
      {(data?.items ?? []).length === 0 && !isLoading && (
        <Empty title="Keine Einträge im gewählten Zeitraum" />
      )}
      <TableFooter table={table} noun="Einträge" />

      {entry && (
        <Dialog open onClose={() => setEntry(null)} title={actionLabel(entry.action)}>
          <div className="flex max-h-[70vh] flex-col gap-3 overflow-y-auto text-sm">
            <p className="muted">
              {dateTime(entry.at)} · {entry.actorEmail || "System"} · {entry.ip}
              {ACTION_LABELS[entry.action] && (
                <span className="ml-2 font-mono text-xs">{entry.action}</span>
              )}
            </p>
            {isActivity(entry) ? (
              <div>
                <p className="font-medium">Details</p>
                <pre className="mt-1 overflow-auto rounded bg-[var(--surface-sunken)] p-3 text-xs">
                  {JSON.stringify(
                    { betrifft: `${entry.targetType} ${entry.targetId || ""}`.trim() || null, ...((entry.after as object) ?? {}) },
                    null,
                    2,
                  )}
                </pre>
              </div>
            ) : (
              <>
                <div>
                  <p className="font-medium">Vorher</p>
                  <pre className="mt-1 overflow-auto rounded bg-[var(--surface-sunken)] p-3 text-xs">
                    {JSON.stringify(entry.before ?? null, null, 2)}
                  </pre>
                </div>
                <div>
                  <p className="font-medium">Nachher</p>
                  <pre className="mt-1 overflow-auto rounded bg-[var(--surface-sunken)] p-3 text-xs">
                    {JSON.stringify(entry.after ?? null, null, 2)}
                  </pre>
                </div>
              </>
            )}
            <div className="flex justify-end">
              <Button onClick={() => setEntry(null)}>Schließen</Button>
            </div>
          </div>
        </Dialog>
      )}
    </Card>
  );
}

// --- synchronisation -----------------------------------------------------

export function SyncPage() {
  const qc = useQueryClient();

  const backlog = useQuery({
    queryKey: ["sync", "backlog"],
    queryFn: () => api<Record<string, number>>("/api/admin/sync/backlog"),
    refetchInterval: 15_000,
  });
  const stuck = useQuery({
    queryKey: ["sync", "stuck"],
    queryFn: () => api<OutboxEntry[]>("/api/admin/sync/stuck?limit=100"),
  });

  const retry = useMutation({
    mutationFn: (id: number) =>
      api<void>(`/api/admin/sync/retry/${id}`, { method: "POST" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["sync"] }),
  });

  const targets = Object.entries(backlog.data ?? {});

  // What is stuck is a bounded list — the server hands out at most a hundred
  // — so it orders and pages here.
  const table = useClientTable(stuck.data, {
    initial: { key: "Attempts", dir: "desc" },
    pageSize: 25,
    accessors: { Target: (e) => targetLabel(e.Target) },
  });

  return (
    <>
      <PageHeader
        title="Synchronisation"
        description="Was noch nicht bei Keycloak und der Central-API angekommen ist."
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        {targets.length === 0 ? (
          <Stat
            label="Rückstau"
            value={0}
            tone="success"
            hint="Alles ausgeliefert."
          />
        ) : (
          targets.map(([target, count]) => (
            <Stat
              key={target}
              label={targetLabel(target)}
              value={count}
              tone={count > 0 ? "warning" : "success"}
              hint="offene Änderungen"
            />
          ))
        )}
      </div>

      <Card className="p-4">
        <h2 className="mb-1 font-medium">Steckengeblieben</h2>
        <p className="mb-3 text-sm muted">
          Einträge, die es nach zehn Versuchen aufgegeben haben. Im Altsystem
          wurden sie danach einfach nicht mehr ausgewählt — ohne jede Meldung.
        </p>

        {stuck.isLoading ? (
          <Spinner />
        ) : (
          <Table>
            <thead>
              <tr>
                <SortableTh table={table} sortKey="Target">
                  Ziel
                </SortableTh>
                <SortableTh table={table} sortKey="Aggregate">
                  Art
                </SortableTh>
                <SortableTh table={table} sortKey="AggregateID">
                  Datensatz
                </SortableTh>
                <SortableTh table={table} sortKey="Kind">
                  Änderung
                </SortableTh>
                <SortableTh
                  table={table}
                  sortKey="Attempts"
                  first="desc"
                  align="right"
                >
                  Versuche
                </SortableTh>
                <Th />
              </tr>
            </thead>
            <tbody>
              {table.rows.map((e) => (
                <tr key={e.ID}>
                  <Td>{targetLabel(e.Target)}</Td>
                  <Td>{e.Aggregate}</Td>
                  <Td className="tabular-nums">{e.AggregateID}</Td>
                  <Td>{e.Kind}</Td>
                  <Td className="text-right tabular-nums">{e.Attempts}</Td>
                  <Td className="text-right">
                    <Button size="sm" onClick={() => retry.mutate(e.ID)}>
                      <RotateCw className="size-4" aria-hidden /> Erneut
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {(stuck.data ?? []).length === 0 && !stuck.isLoading && (
          <Empty title="Nichts steckengeblieben" />
        )}
        <TableFooter table={table} noun="Einträge" />
      </Card>
    </>
  );
}

function targetLabel(t: string): string {
  switch (t) {
    case "keycloak":
      return "Identity-Provider";
    case "central_api":
      return "Central-API";
    default:
      return t;
  }
}

// --- alerts --------------------------------------------------------------

export function AlertsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["alerts"],
    queryFn: () => api<Alert[]>("/api/admin/alerts?limit=100"),
    refetchInterval: 30_000,
  });

  const ack = useMutation({
    mutationFn: (id: number) =>
      api<void>(`/api/admin/alerts/${id}/ack`, { method: "POST" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["alerts"] }),
  });

  // Meldungen are cards rather than rows, but they are still a list: they get
  // the same ordering and the same footer, with a select standing in for the
  // column headers a table would have.
  const table = useClientTable(data, {
    initial: { key: "lastSeen", dir: "desc" },
    pageSize: 25,
    accessors: {
      // Critical before warning before info, so that "nach Dringlichkeit"
      // descending puts what is burning at the top.
      level: (a) => ({ critical: 3, warning: 2, info: 1 })[a.level] ?? 0,
    },
  });

  if (isLoading) return <Spinner />;

  return (
    <>
      <PageHeader
        title="Meldungen"
        description="Was aufgegeben hat und einen Menschen braucht."
      />

      {(data ?? []).length === 0 ? (
        <Notice tone="success">Nichts offen.</Notice>
      ) : (
        <>
          <Toolbar>
            <label className="flex items-center gap-2 text-sm muted">
              sortiert nach
              <Select
                className="w-56"
                value={
                  table.sort
                    ? `${table.sort.key}:${table.sort.dir}`
                    : "lastSeen:desc"
                }
                onChange={(e) => {
                  const [key = "lastSeen", dir] = e.target.value.split(":");
                  table.setSort({ key, dir: dir === "desc" ? "desc" : "asc" });
                }}
                aria-label="Meldungen sortieren"
              >
                <option value="lastSeen:desc">zuletzt gesehen</option>
                <option value="firstSeen:desc">zuerst gesehen</option>
                <option value="level:desc">Dringlichkeit</option>
                <option value="occurrences:desc">Häufigkeit</option>
                <option value="subject:asc">Betreff</option>
                <option value="source:asc">Quelle</option>
              </Select>
            </label>
          </Toolbar>
          <div className="flex flex-col gap-3">
            {table.rows.map((a) => (
              <Card key={a.id} className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Badge
                        tone={a.level === "critical" ? "danger" : "warning"}
                      >
                        {a.level === "critical" ? "kritisch" : "Warnung"}
                      </Badge>
                      <span className="font-medium">{a.subject}</span>
                      {a.occurrences > 1 && (
                        <span className="text-xs muted">{a.occurrences}×</span>
                      )}
                    </div>
                    {a.body && (
                      <pre className="mt-2 whitespace-pre-wrap text-sm muted">
                        {a.body}
                      </pre>
                    )}
                    <p className="mt-2 text-xs muted">
                      zuerst {relative(a.firstSeen)} · zuletzt{" "}
                      {relative(a.lastSeen)} · {a.source}
                    </p>
                  </div>
                  <Button size="sm" onClick={() => ack.mutate(a.id)}>
                    <Check className="size-4" aria-hidden /> Erledigt
                  </Button>
                </div>
              </Card>
            ))}
          </div>
          <Card className="mt-3">
            <TableFooter table={table} noun="Meldungen" />
          </Card>
        </>
      )}
    </>
  );
}

// --- robot console -------------------------------------------------------

// The robot is one shared resource, so its page is the whole system's: it
// does not follow the company switcher. The tab rides in the URL so that a
// link to the script explorer keeps working.
const robotTabs = [
  { id: "calls", label: "Anrufe" },
  { id: "explore", label: "Skripte erstellen" },
  { id: "samples", label: "Samples" },
];

export function RobotPage() {
  const [params, setParams] = useSearchParams();
  const tab = robotTabs.some((t) => t.id === params.get("tab"))
    ? params.get("tab")!
    : "calls";
  const setTab = (id: string) =>
    setParams(id === "calls" ? {} : { tab: id }, { replace: true });
  const [starting, setStarting] = useState(false);
  const [listening, setListening] = useState(false);

  return (
    <>
      <PageHeader
        title="Roboter"
        description="Der Anrufroboter: Anrufe von Hand, Nachkontrollen, und Dialer-Skripte, die er sich selbst erarbeitet."
        actions={
          tab === "calls" ? (
            <>
              <Button onClick={() => setListening(true)} title="Die Mobilbox wie ein Anrufer anrufen und aufnehmen, was sie spielt">
                Nachkontrolle: Box anrufen
              </Button>
              <Button variant="primary" onClick={() => setStarting(true)}>
                <Phone className="size-4" aria-hidden /> Anruf starten
              </Button>
            </>
          ) : undefined
        }
      />

      <Tabs active={tab} onChange={setTab} tabs={robotTabs} />

      {tab === "calls" && <CallsConsole />}
      {tab === "explore" && <ExploreTab />}
      {tab === "samples" && <SamplesCard />}

      {starting && <StartCallDialog onClose={() => setStarting(false)} />}
      {listening && <ListenDialog onClose={() => setListening(false)} />}
    </>
  );
}

function CallsConsole() {
  const qc = useQueryClient();

  const reviews = useQuery({
    queryKey: ["robot-reviews"],
    queryFn: () => api<RobotReview[]>("/api/admin/robot/reviews?limit=100"),
    refetchInterval: 5_000,
  });
  const reviewsByCall = new Map<number, RobotReview[]>();
  for (const r of reviews.data ?? []) {
    if (r.callId) reviewsByCall.set(r.callId, [...(reviewsByCall.get(r.callId) ?? []), r]);
  }

  const { data, isLoading } = useQuery({
    queryKey: ["calls"],
    queryFn: () => api<RobotCall[]>("/api/admin/calls?limit=100"),
    // A call is over in a minute or two; refreshing while the list is open is
    // what makes the console usable.
    refetchInterval: 5_000,
  });

  const cancel = useMutation({
    mutationFn: (id: number) =>
      api<void>(`/api/admin/calls/${id}/cancel`, { method: "POST" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["calls"] }),
  });

  const table = useClientTable(data, {
    initial: { key: "createdAt", dir: "desc" },
    pageSize: 25,
    accessors: {
      state: (c) => callLabel(c),
      durationMs: (c) => c.durationMs || null,
    },
  });

  return (
    <>
      {isLoading ? (
        <Spinner />
      ) : (
        <Card>
          <Table>
            <thead>
              <tr>
                <SortableTh table={table} sortKey="createdAt" first="desc">
                  Zeitpunkt
                </SortableTh>
                <SortableTh table={table} sortKey="msisdn">
                  Nummer
                </SortableTh>
                <SortableTh table={table} sortKey="userName">
                  Person
                </SortableTh>
                <SortableTh table={table} sortKey="scriptName">
                  Skript
                </SortableTh>
                <SortableTh table={table} sortKey="state">
                  Zustand
                </SortableTh>
                <SortableTh table={table} sortKey="durationMs" first="desc">
                  Dauer
                </SortableTh>
                <Th>Mithören</Th>
                <Th>Kontrolle</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {table.rows.map((c) => (
                <tr key={c.id}>
                  <Td className="whitespace-nowrap muted">
                    {dateTime(c.createdAt)}
                  </Td>
                  <Td className="tabular-nums">{c.msisdn}</Td>
                  <Td>{c.userName || "—"}</Td>
                  <Td className="muted">{c.scriptName || "—"}</Td>
                  <Td>
                    <Badge tone={callTone(c)}>{callLabel(c)}</Badge>
                    {c.error && (
                      <span className="ml-2 text-xs muted" title={c.error}>
                        {c.error}
                      </span>
                    )}
                  </Td>
                  <Td className="tabular-nums">
                    {c.durationMs
                      ? `${Math.round(c.durationMs / 1000)} s`
                      : "—"}
                  </Td>
                  <Td>
                    {c.sessionId && (
                      <AudioPlayer
                        src={
                          c.live
                            ? `/api/media/calls/${c.sessionId}/live.mp3`
                            : `/api/media/calls/${c.sessionId}/recording.mp3`
                        }
                        label={c.live ? "läuft gerade" : undefined}
                      />
                    )}
                  </Td>
                  <Td>
                    {(() => {
                      const rs = reviewsByCall.get(c.id) ?? [];
                      const last = rs[0];
                      return (
                        <div className="flex flex-col gap-1">
                          {last && <ReviewLine r={last} />}
                          {c.sessionId && (c.state === "succeeded" || c.state === "failed") && (
                            <span>
                              <ReviewButton
                                path={`/api/admin/calls/${c.id}/review`}
                                queryKey={["robot-reviews"]}
                                label={last ? "Erneut prüfen" : "Prüfen"}
                              />
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </Td>
                  <Td className="text-right">
                    {(c.state === "queued" || c.state === "running") && (
                      <Button size="sm" onClick={() => cancel.mutate(c.id)}>
                        Abbrechen
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
          {(data ?? []).length === 0 && <Empty title="Noch keine Anrufe" />}
          <TableFooter table={table} noun="Anrufe" />
        </Card>
      )}

      <Card className="mt-6">
        <div className="p-4">
          <h2 className="text-base font-semibold">Nachkontrollen</h2>
          <p className="text-sm muted">
            Was die Mobilbox gesagt hat: Transkripte der Roboter-Sitzungen und
            Abhör-Anrufe, die die aktive Ansage so aufnehmen, wie ein Anrufer
            sie hört.
          </p>
        </div>
        <div className="flex flex-col gap-2 px-4 pb-4">
          {(reviews.data ?? []).slice(0, 30).map((r) => (
            <div key={r.id} className="flex flex-wrap items-baseline gap-2">
              <span className="text-xs muted tabular-nums w-28">
                {r.msisdn}
                {r.userName && ` · ${r.userName}`}
              </span>
              <ReviewLine r={r} />
            </div>
          ))}
          {(reviews.data ?? []).length === 0 && <Empty title="Noch keine Kontrolle" />}
        </div>
      </Card>
    </>
  );
}

// --- the robot's sample library --------------------------------------------

const findingLabels: Record<string, string> = {
  unreadable: "nicht lesbar",
  not_pcm: "kein PCM-WAV",
  not_8k_mono: "nicht 8 kHz mono",
  too_short: "zu kurz",
  too_quiet: "zu leise",
  clipping: "übersteuert",
  duplicate: "Duplikat",
  name_vs_transcript: "Name ≠ Transkript",
  empty_transcript: "kein Transkript",
  unreferenced: "von keinem Skript benutzt",
  dtmf_mismatch: "DTMF-Ton ≠ Name",
  uncategorised: "keine Kategorie",
};

function SamplesCard() {
  const qc = useQueryClient();
  const [requested, setRequested] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["robot-samples"],
    queryFn: () =>
      api<{ report: SampleReport | null }>("/api/admin/robot/samples/report"),
    // A check takes a minute or two on the worker; while one was requested
    // the card keeps looking for the result.
    refetchInterval: requested ? 5_000 : false,
  });
  const check = useMutation({
    mutationFn: () =>
      api<void>("/api/admin/robot/samples/check", { method: "POST" }),
    onSuccess: () => setRequested(new Date().toISOString()),
  });
  const report = data?.report ?? null;
  useEffect(() => {
    if (requested && report && report.createdAt > requested) {
      setRequested(null);
      void qc.invalidateQueries({ queryKey: ["alerts"] });
    }
  }, [requested, report, qc]);

  const files = report?.report.files ?? [];
  const flagged = files.filter((f) => f.findings.length > 0 || f.error);
  const table = useClientTable(files, {
    initial: { key: "path", dir: "asc" },
    pageSize: 25,
    accessors: {
      findings: (f) => f.findings.length,
    },
  });

  return (
    <Card className="mt-6">
      <div className="flex flex-wrap items-start justify-between gap-3 p-4">
        <div>
          <h2 className="text-base font-semibold">Samples des Roboters</h2>
          <p className="text-sm muted">
            Die Aufnahmen, mit denen die Dialer-Skripte die Ansagen der Mobilbox
            erkennen: Technik, Whisper-Transkript und ob der Dateiname zum
            Gehörten passt.
            {report && (
              <>
                {" "}
                Zuletzt geprüft {dateTime(report.createdAt)} in{" "}
                <code>{report.workDir}</code>.
              </>
            )}
          </p>
        </div>
        <Button onClick={() => check.mutate()} disabled={check.isPending || !!requested}>
          <RotateCw className="size-4" aria-hidden />{" "}
          {requested ? "Prüfung läuft …" : "Samples prüfen"}
        </Button>
      </div>
      {check.isError && (
        <div className="px-4 pb-4">
          <ErrorNote>Die Prüfung konnte nicht angestoßen werden.</ErrorNote>
        </div>
      )}
      {isLoading ? (
        <Spinner />
      ) : !report ? (
        <Empty
          title="Noch kein Bericht"
          hint={"„Samples prüfen“ lässt den Worker die Bibliothek im Arbeitsverzeichnis des Roboters untersuchen."}
        />
      ) : (
        <>
          <div className="flex flex-wrap gap-4 px-4 pb-4 text-sm">
            <Stat label="Dateien" value={String(report.totals.files ?? 0)} />
            <Stat label="mit Befund" value={String(report.totals.with_findings ?? 0)} />
            <Stat label="fehlend" value={String(report.totals.missing ?? 0)} />
            <Stat label="transkribiert" value={String(report.totals.transcribed ?? 0)} />
          </div>
          {report.report.missing.length > 0 && (
            <div className="px-4 pb-4">
              <Notice tone="warning">
                Skripte laden Samples, die es nicht gibt:{" "}
                {report.report.missing.map((m) => (
                  <span key={m.sample} className="mr-3">
                    <code>{m.sample}</code> ({m.scripts.join(", ")})
                  </span>
                ))}
              </Notice>
            </div>
          )}
          <Table>
            <thead>
              <tr>
                <SortableTh table={table} sortKey="path">
                  Datei
                </SortableTh>
                <SortableTh table={table} sortKey="category">
                  Kategorie (Name)
                </SortableTh>
                <SortableTh table={table} sortKey="llmCategory">
                  Kategorie (Transkript)
                </SortableTh>
                <SortableTh table={table} sortKey="durationMs" first="desc">
                  Dauer
                </SortableTh>
                <SortableTh table={table} sortKey="findings" first="desc">
                  Befunde
                </SortableTh>
                <Th>Transkript</Th>
              </tr>
            </thead>
            <tbody>
              {table.rows.map((f) => (
                <tr key={f.path}>
                  <Td className="font-mono text-xs">{f.path}</Td>
                  <Td>{f.category}</Td>
                  <Td>
                    {f.llmCategory ? (
                      <Badge tone={f.llmCategory === f.category ? "success" : "warning"}>
                        {f.llmCategory}
                      </Badge>
                    ) : (
                      "—"
                    )}
                  </Td>
                  <Td className="tabular-nums">{(f.durationMs / 1000).toFixed(1)} s</Td>
                  <Td>
                    {f.findings.map((code) => (
                      <Badge key={code} tone="danger">
                        {findingLabels[code] ?? code}
                      </Badge>
                    ))}
                    {f.error && (
                      <span className="ml-2 text-xs muted" title={f.error}>
                        {f.error}
                      </span>
                    )}
                  </Td>
                  <Td className="text-xs muted max-w-md truncate" title={f.transcript}>
                    {f.transcript || (f.digit ? `Taste ${f.digit}` : "—")}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
          {files.length === 0 && <Empty title="Keine Samples gefunden" />}
          <TableFooter table={table} noun="Samples" />
          {flagged.length > 0 && (
            <p className="px-4 pb-4 text-xs muted">
              {flagged.length} von {files.length} Dateien mit Befund.
            </p>
          )}
        </>
      )}
    </Card>
  );
}

function callTone(c: RobotCall) {
  switch (c.state) {
    case "succeeded":
      return "success" as const;
    case "failed":
      return "danger" as const;
    case "running":
      return "warning" as const;
    default:
      return "neutral" as const;
  }
}

function callLabel(c: RobotCall): string {
  switch (c.state) {
    case "queued":
      return "wartet";
    case "running":
      return "läuft";
    case "succeeded":
      return "erledigt";
    case "failed":
      return "fehlgeschlagen";
    default:
      return "abgebrochen";
  }
}

// --- the company a console dialog works in --------------------------------
//
// The robot page is system-wide, so a dialog that names a person asks which
// company first, starting from the one the switcher last showed. The people
// of that company are then offered; without a company there is only the
// free number.

/** The company the switcher last showed, or the person's own. */
export function useInitialCompany(): number {
  const { me } = useAuth();
  return me?.scopeCompanyId || me?.companyId || 0;
}

export function useCompanyUsers(companyId: number) {
  return useQuery({
    queryKey: ["users", companyId, "for-call"],
    queryFn: () =>
      api<Page<User>>(`/api/admin/users?limit=500&activeOnly=true&companyId=${companyId}`),
    enabled: companyId > 0,
  });
}

export function CompanyField({
  value,
  onChange,
}: {
  value: number;
  onChange: (id: number) => void;
}) {
  const { data } = useQuery({
    queryKey: ["companies", "all"],
    queryFn: () => api<Page<Company>>("/api/admin/companies?limit=1000"),
  });
  const items: ComboboxItem[] = (data?.items ?? []).map((c) => ({
    value: c.id,
    label: c.name,
    hint: c.domain,
  }));
  return (
    <Field label="Firma" hint="Bestimmt, welche Personen unten zur Wahl stehen.">
      <Combobox
        label="Firma"
        placeholder="Firma wählen"
        searchPlaceholder="Firma suchen …"
        emptyLabel="keine Firma gefunden"
        items={items}
        value={value}
        onChange={(v) => onChange(Number(v))}
      />
    </Field>
  );
}

function ListenDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const initialCompany = useInitialCompany();
  const [companyId, setCompanyId] = useState(initialCompany);
  const [draft, setDraft] = useState({ userId: 0, msisdn: "" });
  const users = useCompanyUsers(companyId);
  const start = useMutation({
    mutationFn: () => api<RobotReview>("/api/admin/robot/listen", { method: "POST", body: draft }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["robot-reviews"] });
      onClose();
    },
  });
  return (
    <Dialog open onClose={onClose} title="Nachkontrolle: die Box wie ein Anrufer anrufen">
      <div className="flex flex-col gap-4">
        <Notice>
          Der Roboter ruft die Mobilbox so an, wie ein Anrufer es tut, nimmt
          auf, was sie spielt, und transkribiert es. Bekannt sind die
          Einwahlen für A1 (0664), Magenta (0676) und HoT (0677).
        </Notice>
        <CompanyField
          value={companyId}
          onChange={(id) => {
            setCompanyId(id);
            setDraft({ ...draft, userId: 0 });
          }}
        />
        <Field label="Person" hint="Leer: die Nummer unten.">
          <Select value={draft.userId} onChange={(e) => setDraft({ ...draft, userId: Number(e.target.value) })}>
            <option value={0}>— freie Nummer —</option>
            {(users.data?.items ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.lastName}, {u.firstName} ({u.msisdn})
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Nummer">
          <Input value={draft.msisdn} onChange={(e) => setDraft({ ...draft, msisdn: e.target.value })} />
        </Field>
        {start.error && <ErrorNote>{(start.error as Error).message}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" disabled={start.isPending} onClick={() => start.mutate()}>
            Anrufen und aufnehmen
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function StartCallDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const initialCompany = useInitialCompany();
  const [draft, setDraft] = useState({
    userId: 0,
    companyId: initialCompany,
    msisdn: "",
    pin: "",
    scriptId: 0,
    audioKey: "",
    note: "",
  });

  const users = useCompanyUsers(draft.companyId);
  const scripts = useQuery({
    queryKey: ["dialer-scripts"],
    queryFn: () => api<DialerScript[]>("/api/admin/dialer-scripts"),
  });

  const start = useMutation({
    mutationFn: () =>
      api<RobotCall>("/api/admin/calls", { method: "POST", body: draft }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["calls"] });
      onClose();
    },
  });

  return (
    <Dialog open onClose={onClose} title="Anruf starten">
      <div className="flex flex-col gap-4">
        <Notice>
          Der Anruf wird vom Worker geführt, nicht von diesem Fenster. Die PIN
          wird verschlüsselt hinterlegt und nach dem Anruf gelöscht.
        </Notice>

        <CompanyField
          value={draft.companyId}
          onChange={(id) => setDraft({ ...draft, companyId: id, userId: 0 })}
        />

        <Field
          label="Person"
          hint="Nummer, Skript und PIN kommen dann von dort."
        >
          <Select
            value={draft.userId}
            onChange={(e) =>
              setDraft({ ...draft, userId: Number(e.target.value) })
            }
          >
            <option value={0}>— freie Nummer —</option>
            {(users.data?.items ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.lastName}, {u.firstName} ({u.msisdn})
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Nummer" hint="Leer: die Nummer der gewählten Person.">
          <Input
            value={draft.msisdn}
            onChange={(e) => setDraft({ ...draft, msisdn: e.target.value })}
          />
        </Field>

        <Field
          label="Mobilbox-PIN"
          hint="Leer: die hinterlegte PIN der Person."
        >
          <Input
            type="password"
            autoComplete="off"
            value={draft.pin}
            onChange={(e) => setDraft({ ...draft, pin: e.target.value })}
          />
        </Field>

        <Field label="Skript">
          <Select
            value={draft.scriptId}
            onChange={(e) =>
              setDraft({ ...draft, scriptId: Number(e.target.value) })
            }
          >
            <option value={0}>— keines —</option>
            {(scripts.data ?? []).map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Notiz">
          <Input
            value={draft.note}
            onChange={(e) => setDraft({ ...draft, note: e.target.value })}
          />
        </Field>

        {start.error && <ErrorNote>{(start.error as Error).message}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button
            variant="primary"
            disabled={start.isPending}
            onClick={() => start.mutate()}
          >
            Anrufen
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// --- system --------------------------------------------------------------

export function SystemPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["system"],
    queryFn: () =>
      api<{ features: Record<string, unknown> }>("/api/admin/system"),
  });

  const features = data?.features ?? {};
  // A map has no order, so the page gives it one and lets it be turned round.
  const settings = useMemo(
    () =>
      Object.entries(features).map(([key, value]) => ({
        key,
        label: featureLabel(key),
        value,
      })),
    [features],
  );
  const table = useClientTable(settings, {
    initial: { key: "label", dir: "asc" },
    pageSize: 50,
    accessors: {
      value: (s) => (typeof s.value === "boolean" ? s.value : String(s.value)),
    },
  });

  if (isLoading) return <Spinner />;

  return (
    <>
      <PageHeader
        title="System"
        description="Wie diese Installation konfiguriert ist."
      />

      {features["simulateProviderCalls"] === true && (
        <div className="mb-4">
          <Notice tone="warning">
            Simulationsmodus: keine Mobilbox wird angefasst, keine SMS und keine
            E-Mail verlassen dieses System.
          </Notice>
        </div>
      )}

      <Card>
        <Table>
          <thead>
            <tr>
              <SortableTh table={table} sortKey="label">
                Einstellung
              </SortableTh>
              <SortableTh table={table} sortKey="value">
                Wert
              </SortableTh>
            </tr>
          </thead>
          <tbody>
            {table.rows.map(({ key, label, value }) => (
              <tr key={key}>
                <Td>{label}</Td>
                <Td>
                  {typeof value === "boolean" ? (
                    <Badge tone={value ? "info" : "neutral"}>
                      {value ? "ja" : "nein"}
                    </Badge>
                  ) : (
                    String(value)
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
        <TableFooter table={table} noun="Einstellungen" />
      </Card>
    </>
  );
}

function featureLabel(key: string): string {
  const labels: Record<string, string> = {
    env: "Umgebung",
    simulateProviderCalls: "Provider simulieren",
    testMsisdn: "Testnummer gesetzt",
    skipAudioRendering: "Rendern überspringen",
    skipBanCheck: "BAN-Prüfung aus",
    disableDefaultGreet: "Standardansage aus",
    billingDryRun: "Abrechnung nur Probelauf",
    billingDriver: "Abrechnungsweg",
    syncDispatchEnabled: "Synchronisation sendet",
    keycloakConfigured: "Identity-Provider konfiguriert",
    centralApiConfigured: "Central-API konfiguriert",
    apiV1Served: "Mobile-App-API aktiv",
    apiV1AcceptsLegacy: "Alt-Token ohne Ablauf akzeptiert",
    apiV2Served: "Connector-API aktiv",
    storageDriver: "Dateiablage",
    alertEmailConfigured: "Meldungen per E-Mail",
  };
  return labels[key] ?? key;
}
